import React from "react";

export type LinkifyPart =
  | { type: "text"; value: string }
  | { type: "link"; href: string; label: string };

const TOKEN_RE =
  /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s]+)/g;

function isHttpUrl(href: string): boolean {
  try {
    const url = new URL(href);
    return url.protocol === "http:" || url.protocol === "https:";
  } catch {
    return false;
  }
}

function splitUrlAndTrailing(raw: string): { href: string; trailing: string } {
  let href = raw;
  let trailing = "";

  while (href && /[.,;:!?`>]$/.test(href)) {
    trailing = href.slice(-1) + trailing;
    href = href.slice(0, -1);
  }

  while (
    href.endsWith(")") &&
    (href.match(/\(/g) || []).length < (href.match(/\)/g) || []).length
  ) {
    trailing = ")" + trailing;
    href = href.slice(0, -1);
  }

  return { href, trailing };
}

export function parseLinkifyParts(text: string): LinkifyPart[] {
  const parts: LinkifyPart[] = [];
  let lastIndex = 0;
  TOKEN_RE.lastIndex = 0;

  let match: RegExpExecArray | null;
  while ((match = TOKEN_RE.exec(text)) !== null) {
    const index = match.index;
    if (index > lastIndex) {
      parts.push({ type: "text", value: text.slice(lastIndex, index) });
    }

    const markdownLabel = match[1];
    const markdownHref = match[2];
    const bareUrl = match[3];

    if (markdownHref) {
      if (isHttpUrl(markdownHref)) {
        parts.push({
          type: "link",
          href: markdownHref,
          label: markdownLabel || markdownHref,
        });
      } else {
        parts.push({ type: "text", value: match[0] });
      }
      lastIndex = index + match[0].length;
      continue;
    }

    if (!bareUrl) {
      lastIndex = index + match[0].length;
      continue;
    }

    const { href, trailing } = splitUrlAndTrailing(bareUrl);
    if (isHttpUrl(href)) {
      parts.push({ type: "link", href, label: href });
      if (trailing) {
        parts.push({ type: "text", value: trailing });
      }
    } else {
      parts.push({ type: "text", value: match[0] });
    }
    lastIndex = index + match[0].length;
  }

  if (lastIndex < text.length) {
    parts.push({ type: "text", value: text.slice(lastIndex) });
  }

  return parts;
}

export function LinkifiedText({ text }: { text: string | null | undefined }) {
  if (!text) {
    return null;
  }

  return (
    <>
      {parseLinkifyParts(text).map((part, index) =>
        part.type === "link" ? (
          <a
            key={index}
            href={part.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-700 underline break-all"
            onClick={(event) => event.stopPropagation()}
          >
            {part.label}
          </a>
        ) : (
          <React.Fragment key={index}>{part.value}</React.Fragment>
        ),
      )}
    </>
  );
}
